import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Panel } from "@/components/ui/Panel";
import { formatDate } from "@/domain/datetime";

/**
 * Khối "Chứng từ gần đây" trên trang chi tiết khách hàng và nhà cung cấp (18/09/2026): mỗi cột một loại
 * chứng từ, 5 cái mới nhất, "Xem tất cả" mở danh sách đầy đủ đã lọc sẵn. Hai trang dùng chung khung này để
 * trông và hành xử giống hệt nhau. `columns` đã bỏ sẵn loại tài khoản không được xem; không còn cột nào
 * thì cả khối ẩn.
 */
export function RecentDocumentsPanel({ title, hint, columns }: { title: string; hint: string; columns: React.ReactNode[] }) {
  if (columns.length === 0) return null;
  return (
    <Panel className="p-5">
      <h2 className="text-lg font-semibold text-ink">{title}</h2>
      <p className="mt-1 text-xs text-ink-soft">{hint}</p>
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">{columns}</div>
    </Panel>
  );
}

export function DocumentColumn({ title, total, loading, error, allTo, children }: { title: string; total?: number; loading: boolean; error: unknown; allTo: string; children?: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <section className="min-w-0 rounded-md border border-line bg-paper">
      <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
        <h3 className="text-sm font-semibold text-ink">
          {title} {total != null && <span className="font-mono tabular text-xs font-normal text-ink-soft">({total})</span>}
        </h3>
        {!!total && (
          <Link to={allTo} className="text-xs text-brand hover:underline">
            {t("common.viewAll")}
          </Link>
        )}
      </div>
      {loading ? (
        <p className="px-3 py-3 text-sm text-ink-soft">{t("common.loading")}</p>
      ) : error ? (
        <p role="alert" className="px-3 py-3 text-sm text-danger">{t("common.documentsError")}</p>
      ) : !total ? (
        <p className="px-3 py-3 text-sm text-ink-soft">{t("common.noDocuments")}</p>
      ) : (
        <ul className="divide-y divide-line">{children}</ul>
      )}
    </section>
  );
}

/** `date`: mốc thời gian (ms) hoặc ngày `YYYY-MM-DD` như hoá đơn nhà cung cấp lưu. */
export function DocumentRow({ to, no, date, money, children }: { to: string; no: string; date: number | string | null; money: string; children?: React.ReactNode }) {
  return (
    <li className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
      <div className="min-w-0">
        <Link to={to} className="font-mono tabular font-medium text-brand hover:underline">{no}</Link>
        <p className="font-mono tabular text-xs text-ink-soft">{typeof date === "string" ? date : formatDate(date)}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="font-mono tabular">{money}</span>
        {children}
      </div>
    </li>
  );
}
